import { useState, Fragment } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack, Divider, Typography } from '@mui/material';

import DotIcon from '@/components/icons/compliance/DotIcon';

interface Platform {
  label: string;
  value: number;
  color: string;
}

interface Type {
  label: string;
  value: number;
  color: string;
  key: string; // This will link the type to its platform
}

interface ExpandableLegendProps {
  platforms: Platform[];
  types: Type[];
}

const SUBTLE_TEXT_COLOR = 'var(--Light-Surfaces-Text-Surface-Subtle, #757575)';

const getHoverSx = (scale: number) => ({
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': { transform: `scale(${scale})` }
});

const ExpandableLegend = ({ platforms, types }: ExpandableLegendProps) => {
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

  const handleExpandClick = (index: number) =>
    setExpandedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );

  const groupedTypes = platforms.map((platform) => ({
    ...platform,
    types: types.filter((type) => type.key === platform.label && type.value > 0)
  }));

  return (
    <Stack width="100%">
      <Stack direction="row" justifyContent="space-between">
        <Typography fontWeight={500}>Platform</Typography>
        <Typography fontWeight={500}>Posts</Typography>
      </Stack>
      {groupedTypes.map((platform, index) => {
        const hasTypes = platform.types.length > 0;
        const isExpanded = expandedIndices.includes(index);
        const ExpandIcon = isExpanded ? ExpandLessIcon : ExpandMoreIcon;

        return (
          <Fragment key={platform.label}>
            <Stack
              sx={hasTypes ? getHoverSx(1.02) : undefined}
              pt="9.5px"
              pb="9.5px"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={hasTypes ? () => handleExpandClick(index) : undefined}
            >
              <Stack direction="row" alignItems="center" gap="5px">
                <ExpandIcon sx={{ fontSize: 16, transform: 'scale(1, 0.8)' }} />
                <DotIcon fontSize="inherit" sx={{ color: platform.color }} />
                <Typography>{platform.label}</Typography>
              </Stack>
              <Typography color={SUBTLE_TEXT_COLOR}>
                {platform.value}
              </Typography>
            </Stack>

            {isExpanded && hasTypes && (
              <Stack pl="10px">
                {platform.types.map((type) => (
                  <Stack
                    key={type.label}
                    sx={getHoverSx(1.01)}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb="9.5px"
                  >
                    <Stack direction="row" alignItems="center" gap="5px">
                      <DotIcon fontSize="inherit" sx={{ color: type.color }} />
                      <Typography>{type.label}</Typography>
                    </Stack>
                    <Typography color={SUBTLE_TEXT_COLOR}>
                      {type.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {index < groupedTypes.length - 1 && <Divider />}
          </Fragment>
        );
      })}
    </Stack>
  );
};

export default ExpandableLegend;
